import React, { useState } from 'react';
import { Settings as SettingsIcon, Zap, Mail, ShieldAlert, Check, X, Bot, Users, UserPlus, Lock, Unlock, Edit2, Tags, Trash2 } from 'lucide-react';
import { useAuth, ROLES } from '../context/AuthContext';
import { useCRM } from '../context/CRMContext';

const Settings = () => {
  const { user } = useAuth();
  const { logActivity, policyTypes = [], addPolicyType, deletePolicyType } = useCRM();
  
  const [activeTab, setActiveTab] = useState('users');

  // --- Dynamic Policies State ---
  const [newPolicyName, setNewPolicyName] = useState('');
  const [newPolicyColor, setNewPolicyColor] = useState('#2563eb');
  const [isAddingPolicy, setIsAddingPolicy] = useState(false);

  const handleCreatePolicyType = async (e) => {
    e.preventDefault();
    if(!newPolicyName) return;
    setIsAddingPolicy(true);
    await addPolicyType({ name: newPolicyName, color: newPolicyColor });
    setNewPolicyName('');
    setNewPolicyColor('#2563eb');
    setIsAddingPolicy(false);
  };
  
  // ============================================
  // Mocks de Usuarios (Control de Accesos)
  // ============================================
  const [internalUsers, setInternalUsers] = useState([
    { id: 1, name: 'Gerente General', email: 'admin@veritasvas.cl', role: ROLES.ADMIN, status: 'Activo', lastLogin: 'Hace 5 min' },
    { id: 2, name: 'Ejecutivo 1', email: 'ventas@veritasvas.cl', role: ROLES.EDITOR, status: 'Activo', lastLogin: 'Ayer' },
    { id: 3, name: 'Analista 1', email: 'analista@veritasvas.cl', role: ROLES.VIEWER, status: 'Activo', lastLogin: 'Hace 2 días' },
    { id: 4, name: 'Revisor de Riesgo', email: 'riesgo@veritasvas.cl', role: ROLES.VALIDATOR, status: 'Activo', lastLogin: 'Hace 1 hora' },
  ]);

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({ name: '', email: '', role: ROLES.EDITOR });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editRole, setEditRole] = useState('');

  const startEdit = (userObj) => {
    setEditingUserId(userObj.id);
    setEditRole(userObj.role);
  };

  const saveEdit = (userObj) => {
    if (editRole !== userObj.role) {
      logActivity(`El Administrador modificó el rol de ${userObj.name} a ${editRole}.`);
    }
    setInternalUsers(internalUsers.map(u => u.id === userObj.id ? { ...u, role: editRole } : u));
    setEditingUserId(null);
  };

  const handleInviteUser = (e) => {
    e.preventDefault();
    if(!inviteData.name || !inviteData.email) return;

    const newUser = {
      id: Date.now(),
      name: inviteData.name,
      email: inviteData.email,
      role: inviteData.role,
      status: 'Invitación Pendiente',
      lastLogin: 'Nunca'
    };

    setInternalUsers([...internalUsers, newUser]);
    logActivity(`El Administrador invitó a ${inviteData.name} (${inviteData.email}) con perfil de ${inviteData.role}.`);
    setShowInviteForm(false);
    setInviteData({ name: '', email: '', role: ROLES.EDITOR });
    alert(`Invitación corporativa enviada a ${inviteData.email}`);
  };

  const handleToggleUserStatus = (id, currentStatus) => {
    const isBlocking = currentStatus === 'Activo';
    setInternalUsers(internalUsers.map(u => {
      if(u.id === id) {
        logActivity(`El Administrador ${isBlocking ? 'bloqueó' : 'desbloqueó'} el acceso de ${u.name}.`);
        return { ...u, status: isBlocking ? 'Bloqueado' : 'Activo' };
      }
      return u;
    }));
  };

  // ============================================
  // Mocks de Reglas de Negocio
  // ============================================
  const [rules, setRules] = useState([
    {
      id: 1, 
      title: 'Recordatorio de Vencimiento',
      desc: 'Envía un email automático al cliente faltando 30 días para su renovación.',
      icon: Mail,
      active: false
    },
    {
      id: 2, 
      title: 'Alerta de Siniestro Riesgoso',
      desc: 'Notificar al Administrador si se ingresa un reclamo mayor a $5,000.',
      icon: ShieldAlert,
      active: true
    },
    {
      id: 3,
      title: 'Auto-Asignación de Leads Nuevos',
      desc: 'Repartir el prospecto automáticamente al primer editor disponible (Round Robin).',
      icon: Zap,
      active: true
    },
    {
      id: 4,
      title: 'Motor Autovalidador AI (Polizas)',
      desc: 'La IA de Veritas leerá los metadatos de los PDFs de póliza subidos para alertar de incongruencias previo a la validación.',
      icon: Bot,
      active: true
    }
  ]);

  const toggleRule = (id) => {
    setRules(rules.map(r => {
      if (r.id === id) {
        const newState = !r.active;
        logActivity(`Configuración Alterada: Regla "${r.title}" ${newState ? 'encendida' : 'apagada'}.`);
        return { ...r, active: newState };
      }
      return r;
    }));
  };

  // Solo debe verse para Administradores
  if (user?.role !== ROLES.ADMIN) {
    return (
      <div style={{ textAlign: 'center', marginTop: '4rem' }}>
        <h2>Acceso Denegado</h2>
        <p className="text-muted">Solo los Administradores pueden ver configuraciones.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <SettingsIcon size={28} color="var(--primary)" />
        <div>
          <h1>Configuraciones Globales</h1>
          <p className="text-muted">Administra el acceso, equipo y los flujos automáticos de VeritasCRM.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '2rem' }}>
        {/* Menú Izquierdo Settings */}
        <div style={{ width: '250px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            
            <button 
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                textAlign: 'left', padding: '1rem', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                backgroundColor: activeTab === 'users' ? 'var(--primary)' : 'transparent',
                color: activeTab === 'users' ? 'white' : 'var(--text-main)',
                fontWeight: activeTab === 'users' ? 600 : 500
              }}
              onClick={() => setActiveTab('users')}
            >
              <Users size={18} />
              Gestión de Accesos
            </button>

            <button 
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                textAlign: 'left', padding: '1rem', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                backgroundColor: activeTab === 'automations' ? 'var(--primary)' : 'transparent',
                color: activeTab === 'automations' ? 'white' : 'var(--text-main)',
                fontWeight: activeTab === 'automations' ? 600 : 500
              }}
              onClick={() => setActiveTab('automations')}
            >
              <Zap size={18} />
              Automatizaciones
            </button>

            <button 
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                textAlign: 'left', padding: '1rem', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                backgroundColor: activeTab === 'general' ? 'var(--primary)' : 'transparent',
                color: activeTab === 'general' ? 'white' : 'var(--text-main)',
                fontWeight: activeTab === 'general' ? 600 : 500
              }}
              onClick={() => setActiveTab('general')}
            >
              <SettingsIcon size={18} />
              General & Compañía
            </button>

            <button 
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                textAlign: 'left', padding: '1rem', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                backgroundColor: activeTab === 'products' ? 'var(--primary)' : 'transparent',
                color: activeTab === 'products' ? 'white' : 'var(--text-main)',
                fontWeight: activeTab === 'products' ? 600 : 500
              }}
              onClick={() => setActiveTab('products')}
            >
              <Tags size={18} />
              Seguros & Productos
            </button>

          </div>
        </div>

        {/* Contenido Derecho */}
        <div style={{ flex: 1 }}>
          
          {/* TAB: GESTIÓN DE ACCESOS */}
          {activeTab === 'users' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ marginBottom: '0.25rem' }}>Directorio de Empleados</h2>
                  <p className="text-muted" style={{ fontSize: '0.9rem' }}>Añade o revoca accesos de tu equipo a nivel plataforma.</p>
                </div>
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowInviteForm(!showInviteForm)}
                >
                  <UserPlus size={18} /> {showInviteForm ? 'Cancelar' : 'Invitar Empleado'}
                </button>
              </div>

              {/* Formulario Inline de Invitación */}
              {showInviteForm && (
                <form className="card animate-fade-in" onSubmit={handleInviteUser} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', backgroundColor: 'var(--bg-color)', border: '1px solid var(--primary)' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Nombre Completo</label>
                    <input required type="text" placeholder="Ej: Pedro Soto" value={inviteData.name} onChange={e => setInviteData({...inviteData, name: e.target.value})} style={{ width: '100%', padding: '0.6rem', borderRadius: '4px', border: '1px solid var(--border)' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Correo Corporativo</label>
                    <input required type="email" placeholder="psoto@veritasvas.cl" value={inviteData.email} onChange={e => setInviteData({...inviteData, email: e.target.value})} style={{ width: '100%', padding: '0.6rem', borderRadius: '4px', border: '1px solid var(--border)' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Nivel de Acceso</label>
                    <select value={inviteData.role} onChange={e => setInviteData({...inviteData, role: e.target.value})} style={{ width: '100%', padding: '0.6rem', borderRadius: '4px', border: '1px solid var(--border)' }}>
                      <option value={ROLES.EDITOR}>Ejecutivo y Ventas</option>
                      <option value={ROLES.VALIDATOR}>Validador de Riesgo</option>
                      <option value={ROLES.VIEWER}>Analista (Solo Lectura)</option>
                      <option value={ROLES.ADMIN}>Administrador Total</option>
                    </select>
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ padding: '0.6rem 1.5rem' }}>Enviar Invitación</button>
                </form>
              )}

              {/* Tabla de Usuarios */}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--bg-color)', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '1rem', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>Usuario</th>
                      <th style={{ padding: '1rem', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>Rol</th>
                      <th style={{ padding: '1rem', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>Última Conexión</th>
                      <th style={{ padding: '1rem', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>Estado</th>
                      <th style={{ padding: '1rem', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, textAlign: 'right' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {internalUsers.map(u => (
                      <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '1rem' }}>
                          <p style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.2rem' }}>{u.name}</p>
                          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{u.email}</p>
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.9rem' }}>
                          {editingUserId === u.id ? (
                            <select value={editRole} onChange={e => setEditRole(e.target.value)} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid var(--primary)', fontSize: '0.8rem' }}>
                              <option value={ROLES.EDITOR}>Ejecutivo</option>
                              <option value={ROLES.VALIDATOR}>Validador</option>
                              <option value={ROLES.VIEWER}>Analista</option>
                              <option value={ROLES.ADMIN}>Administrador</option>
                            </select>
                          ) : (
                            <span style={{ 
                              padding: '0.3rem 0.6rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600,
                              backgroundColor: u.role === ROLES.ADMIN ? 'var(--primary)' : 
                                              u.role === ROLES.VALIDATOR ? 'var(--success)' :
                                              u.role === ROLES.EDITOR ? '#3b82f6' : 'var(--secondary)',
                              color: u.role === ROLES.VIEWER ? 'var(--text-main)' : 'white'
                            }}>
                              {u.role}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>{u.lastLogin}</td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', fontWeight: 500, color: u.status === 'Activo' ? 'var(--success)' : u.status === 'Bloqueado' ? 'var(--danger)' : 'var(--warning)' }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: u.status === 'Activo' ? 'var(--success)' : u.status === 'Bloqueado' ? 'var(--danger)' : 'var(--warning)' }}></div>
                            {u.status}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'right' }}>
                          {editingUserId === u.id ? (
                            <>
                              <button onClick={() => saveEdit(u)} style={{ background: 'none', border: '1px solid var(--success)', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', color: 'var(--success)', marginRight: '0.4rem' }} title="Guardar">
                                <Check size={16} />
                              </button>
                              <button onClick={() => setEditingUserId(null)} style={{ background: 'none', border: '1px solid var(--danger)', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', color: 'var(--danger)' }} title="Cancelar">
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                onClick={() => startEdit(u)}
                                disabled={u.role === ROLES.ADMIN}
                                style={{ 
                                  background: 'none', border: '1px solid var(--border)', padding: '0.4rem', borderRadius: '4px', cursor: u.role === ROLES.ADMIN ? 'not-allowed' : 'pointer',
                                  color: 'var(--text-main)', opacity: u.role === ROLES.ADMIN ? 0.3 : 1, marginRight: '0.4rem' 
                                }}
                                title="Editar Rol"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button 
                                onClick={() => handleToggleUserStatus(u.id, u.status)}
                                disabled={u.role === ROLES.ADMIN}
                                style={{ 
                                  background: 'none', border: '1px solid var(--border)', padding: '0.4rem', borderRadius: '4px', cursor: u.role === ROLES.ADMIN ? 'not-allowed' : 'pointer',
                                  color: u.status === 'Activo' ? 'var(--danger)' : 'var(--success)', opacity: u.role === ROLES.ADMIN ? 0.3 : 1 
                                }}
                                title={u.status === 'Activo' ? 'Bloquear Acceso' : 'Restaurar Acceso'}
                              >
                                {u.status === 'Activo' ? <Lock size={16} /> : <Unlock size={16} />}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB: GENERAL */}
          {activeTab === 'general' && (
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <h2>Datos de la Organización</h2>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>Nombre Comercial</label>
                <input readOnly value="Veritas Vas Corredores de Seguros" style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-color)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>Zona Horaria</label>
                <input readOnly value="Santiago (America/Santiago)" style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-color)' }} />
              </div>
            </div>
          )}

          {/* TAB: AUTOMATIZACIONES */}
          {activeTab === 'automations' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <h2 style={{ marginBottom: '0.5rem' }}>Reglas Inteligentes</h2>
              
              {rules.map(rule => {
                const Icon = rule.icon;
                return (
                  <div key={rule.id} className="card" style={{ 
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                    padding: '1.5rem', borderLeft: rule.active ? '4px solid var(--success)' : '4px solid var(--border)' 
                  }}>
                    <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                      <div className="avatar" style={{ backgroundColor: rule.active ? 'var(--success-bg)' : 'var(--bg-color)', color: rule.active ? 'var(--success)' : 'var(--text-muted)' }}>
                        <Icon size={20} />
                      </div>
                      <div>
                        <h3 style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>{rule.title}</h3>
                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>{rule.desc}</p>
                      </div>
                    </div>

                    <button 
                      onClick={() => toggleRule(rule.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '50px',
                        border: 'none', cursor: 'pointer', transition: 'all 0.2s', fontWeight: 600,
                        backgroundColor: rule.active ? 'var(--success)' : 'var(--secondary)',
                        color: rule.active ? 'white' : 'var(--text-muted)'
                      }}
                    >
                      {rule.active ? (
                        <>Activado <Check size={16} /></>
                      ) : (
                        <>Desactivado <X size={16} /></>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* TAB: SEGUROS Y PRODUCTOS (DINÁMICOS) */}
          {activeTab === 'products' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ marginBottom: '0.25rem' }}>Ecosistema de Seguros</h2>
                  <p className="text-muted" style={{ fontSize: '0.9rem' }}>Gestiona los productos que tu correduría ofrece. Todo el sistema hereda esto en tiempo real.</p>
                </div>
              </div>

              <div className="card" style={{ display: 'flex', gap: '2rem' }}>
                {/* Formulario de Inserción */}
                <div style={{ flex: 1, borderRight: '1px solid var(--border)', paddingRight: '2rem' }}>
                  <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>Añadir Nuevo Producto</h3>
                  <form onSubmit={handleCreatePolicyType} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Nombre del Seguro / Categoría</label>
                      <input 
                        type="text" 
                        value={newPolicyName} 
                        onChange={e => setNewPolicyName(e.target.value)}
                        placeholder="Ej. Transporte Naviero" 
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid var(--border)' }} 
                        required 
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Color Identificativo (Visualización)</label>
                      <input 
                        type="color" 
                        value={newPolicyColor} 
                        onChange={e => setNewPolicyColor(e.target.value)}
                        style={{ width: '100px', height: '40px', padding: '0', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer' }} 
                      />
                    </div>
                    <button type="submit" disabled={isAddingPolicy} className="btn btn-primary" style={{ marginTop: '0.5rem', justifyContent: 'center' }}>
                      {isAddingPolicy ? 'Inyectando...' : 'Guardar y Publicar Categoria'}
                    </button>
                  </form>
                </div>

                {/* Lista Actual */}
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>Productos Activos ({policyTypes.length})</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '300px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                    {policyTypes.map(pt => (
                      <div key={pt.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--bg-color)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: pt.color }}></div>
                          <span style={{ fontWeight: 600 }}>{pt.name}</span>
                        </div>
                        <button 
                          onClick={() => { if(window.confirm(`¿Seguro que deseas eliminar la póliza "${pt.name}"? Los datos atados a ella no se borrarán pero perderás la categorización.`)) deletePolicyType(pt.id, pt.name); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '0.2rem' }}
                          title="Eliminar Cobertura"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    {policyTypes.length === 0 && <p className="text-muted" style={{ fontSize: '0.9rem' }}>Aún no hay seguros configurados.</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Settings;
